import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import axios from 'axios';

import Navigation from '../components/Navigation';
import { usePageMeta, PAGE_META } from '../utils/usePageMeta';
import { startComposioConnection } from '../services/composio';
import { luxuryTheme as theme } from '../styles/theme';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: white;
`;

const LeftPanel = styled.div`
  flex: 1;
  background: linear-gradient(5deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[64]};
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 0 100px rgba(0,0,0,0.2);

  @media (max-width: 1024px) {
    display: none;
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, rgba(255,255,255,0.1) 0%, transparent 70%);
`;

const BrandingContent = styled.div`
  color: white;
  text-align: center;
  position: relative;
  z-index: 1;
  max-width: 500px;
`;

const BrandMessage = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  opacity: 0.9;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[40]};
  background: white;
`;

const LoginContainer = styled(motion.div)`
  width: 100%;
  max-width: 440px;
  padding: ${theme.spacing[48]};
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12),
              0 2px 8px rgba(0, 0, 0, 0.06),
              0 0 1px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.12),
                0 4px 16px rgba(0, 0, 0, 0.08),
                0 0 2px rgba(0, 0, 0, 0.08);
  }
`;

const LoadingSpinner = styled(motion.div)`
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255,255,255,0.6);
  border-top-color: #fff;
  border-radius: 50%;
`;

const GoogleButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #4285F4;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  margin: 0 auto;
  width: fit-content;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: background 0.2s ease;

  & .ripple {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.85);
    transform: scale(0);
    animation: ripple 9s ease-out;
    pointer-events: none;
  }

  @keyframes ripple {
    to {
      transform: scale(8);
      opacity: 0;
    }
  }

  &:hover {
    background: #357ae8;
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

const WelcomeText = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[40]};

  h2 {
    font-size: 2rem;
    font-weight: 700;
    color: ${theme.colors.text};
    margin-bottom: ${theme.spacing[16]};
  }

  p {
    color: ${theme.colors.gray600};
    font-size: 1.125rem;
    line-height: 1.5;
  }
`;

const NotificationBox = styled(motion.div)`
  padding: ${theme.spacing[16]} ${theme.spacing[20]};
  margin-bottom: ${theme.spacing[24]};
  border-radius: 8px;
  text-align: center;
  font-size: 0.95rem;
  line-height: 1.5;

  &.success {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  }

  &.info {
    background: #d1ecf1;
    color: #0c5460;
    border: 1px solid #bee5eb;
  }

  &.error {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
`;


export const LoginPage = () => {
  usePageMeta(PAGE_META.login);
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    // Parse URL parameters
    const params = new URLSearchParams(location.search);
    const token = params.get('invitation_token');
    const teamJoined = params.get('team_joined');
    const company = params.get('company');
    const error = params.get('error');
    const message = params.get('message');

    // Store invitation token for later use
    if (token) {
      localStorage.setItem('pendingInvitationToken', token);
    }

    // Show appropriate notifications
    if (teamJoined === 'true' && company) {
      setNotification({
        type: 'success',
        message: `You've been successfully added to ${company}! Please sign in to continue.`
      });
    } else if (token && company) {
      setNotification({
        type: 'info',
        message: `You've been invited to join ${company}. Sign in or sign up with Google to accept the invitation.`
      });
    } else if (message) {
      setNotification({
        type: 'info',
        message: decodeURIComponent(message)
      });
    } else if (error) {
      const errorMessages = {
        'invalid_invitation': 'This invitation link is invalid or has expired.',
        'join_failed': 'Failed to join the team. Please try again.',
        'invitation_error': 'There was an error processing your invitation.'
      };
      setNotification({
        type: 'error',
        message: errorMessages[error] || 'An error occurred.'
      });
    }
  }, [location]);

  // Check for pending invitation token after successful login
  useEffect(() => {
    const checkPendingInvitation = async () => {
      const token = localStorage.getItem('pendingInvitationToken');
      const authToken = localStorage.getItem('auth_token');

      if (token && authToken) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/team/accept-invitation`,
            { invitation_token: token },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );

          if (response.data.success) {
            localStorage.removeItem('pendingInvitationToken');
            // Navigate to dashboard or show success
            if (response.data.company_name) {
              setNotification({
                type: 'success',
                message: `Welcome to ${response.data.company_name}!`
              });
            }
          }
        } catch (error) {
          console.error('Failed to accept invitation:', error);
        }
      }
    };

    // Check after a short delay to ensure auth is set
    setTimeout(checkPendingInvitation, 1000);
  }, []);

  const createRipple = (event) => {
    const button = event.currentTarget;
    const circle = document.createElement('span');
    circle.style.position = 'absolute';
    const diameter = Math.max(button.clientWidth, button.clientHeight) * 2;
    const radius = diameter / 2;
    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${event.clientX - button.getBoundingClientRect().left - radius}px`;
    circle.style.top = `${event.clientY - button.getBoundingClientRect().top - radius}px`;
    circle.classList.add('ripple');
    const ripple = button.getElementsByClassName('ripple')[0];
    if (ripple) ripple.remove();
    button.appendChild(circle);
  };

  const handleGoogleClick = (e) => {
    if (isLoading) return;
    createRipple(e);
    setIsLoading(true);
    setTimeout(() => startComposioConnection(), 5000); // slight delay for effect
  };


  return (
    <><Navigation /><PageContainer as="main" className="brand-auth brand-container" id="main-content" tabIndex="-1">
      <LeftPanel className="brand-auth-art">
        <GradientOverlay className="brand-auth-overlay" />
        <BrandingContent className="brand-auth-message">
          <span className="brand-eyebrow">GrupaAI / Sign in</span>
          <h1 className="brand-auth-heading">Your business.<br /><span>On autopilot.</span></h1>
          <BrandMessage>
            Your workers working 24/7 to grow your business.
          </BrandMessage>
        </BrandingContent>
      </LeftPanel>

      <RightPanel className="brand-auth-access">
        <LoginContainer className="brand-auth-form"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {notification && (
            <NotificationBox
              className={notification.type}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {notification.message}
            </NotificationBox>
          )}

          <WelcomeText className="brand-auth-welcome">
            <h2>Welcome to GrupaAI</h2>
            <p>Sign in with Google to get started</p>
          </WelcomeText>

          <GoogleButton
              whileTap={{ scale: 0.9 }}
              onClick={handleGoogleClick}
               disabled={isLoading}
              style={{ borderRadius: '8px' }}
            >
              {isLoading ? (
                <LoadingSpinner
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
              ) : (
                <>
                  <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google logo" />
                  Continue with Google
                </>
              )}
            </GoogleButton>
            <p className="brand-auth-legal"><Link to="/privacy">Privacy</Link><span> / </span><Link to="/terms">Terms</Link></p>
        </LoginContainer>
      </RightPanel>
    </PageContainer></>
  );
};

export default LoginPage;
